import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, Typography, Button, Input, Space, Progress, message, Upload,
  Tabs, Form, Select, Tag, Collapse, Badge, Drawer, FloatButton, Avatar,
  Divider, Row, Col, Tooltip, Alert, Switch, List, Modal, Spin, Radio,
} from 'antd';
import {
  RocketOutlined, TeamOutlined, HomeOutlined, ShopOutlined,
  MessageOutlined, QuestionCircleOutlined, ToolOutlined, AimOutlined,
  SendOutlined, UploadOutlined, CheckCircleOutlined, CloudUploadOutlined,
  FacebookOutlined, GoogleOutlined, InstagramOutlined, YoutubeOutlined,
  GlobalOutlined, MailOutlined, FileTextOutlined, VideoCameraOutlined,
  RobotOutlined, CloseOutlined, DeleteOutlined, PaperClipOutlined,
  PhoneOutlined, UserOutlined, BulbOutlined, SafetyOutlined,
  PlayCircleOutlined, StarOutlined, ThunderboltOutlined, HeartOutlined,
  CommentOutlined, PlusOutlined, SaveOutlined, SmileOutlined,
  TranslationOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api/v1'
  : '/api/v1';

const CHATBOT_API = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api/v1'
  : '/api/v1';

// ═══════════════════════════════════════════════════════════
// i18n — EN / ZH-TW / TH
// ═══════════════════════════════════════════════════════════

const LANG_OPTIONS = [
  { key: 'en', label: 'EN', flag: '🇺🇸' },
  { key: 'zh', label: '繁中', flag: '🇹🇼' },
  { key: 'th', label: 'ไทย', flag: '🇹🇭' },
];

const UI = {
  en: {
    partnerPortal: 'Partner Portal',
    progress: 'Progress',
    aiAssistant: 'AI Assistant',
    signIn: 'Sign in',
    sections: 'Sections',
    welcome: 'Welcome',
    thankYouTitle: 'Thank you, {name}!',
    thankYouSubtitle: 'Your onboarding survey is complete',
    thankYouLine1: 'Our HOUSE AI team has received your responses and media channel inputs. We are now building a custom AI playbook tailored to your business.',
    thankYouLine2: 'You\'ll hear from us within the next few business days with the next steps. In the meantime, feel free to reach out anytime.',
    thankYouContact: 'Contact us',
    thankYouSignature: '— Comboware HOUSE AI Team',
    mediaAudit: 'Media Audit',
    saveSection: 'Save This Section',
    saveMedia: 'Save Media Channels',
    savedOk: 'Saved successfully!',
    mediaOk: 'Media channels saved!',
    saveFail: 'Save failed',
    uploaded: 'uploaded!',
    uploadFail: 'Upload failed',
    fileDeleted: 'File deleted',
    deleteFail: 'Delete failed',
    clickUpload: 'Click or Drag to Upload',
    selectPlaceholder: 'Select...',
    typePlaceholder: 'Type your answer...',
    previous: '← Previous',
    nextSection: 'Next Section →',
    completeAll: 'Complete Survey ✓',
    completedMsg: 'Thank you! Your survey is complete. We will review your responses and contact you soon.',
    saveAndNext: 'Save & Continue →',
    startSurvey: 'Start Survey →',
    chatQuestion: 'Have questions? Chat with our AI assistant',
    askAnything: 'Ask me anything...',
    connectionError: 'Connection error. Please try again.',
    loading: 'Loading HOUSE AI Portal...',
    sampleVideos: 'Sample Videos for AI Reference',
    uploadVideos: 'Upload Sample Sales Videos',
    toggleOn: 'Toggle on if you use this channel',
    mediaAlertTitle: 'We will integrate AI into every channel you currently use — making each one 10x more effective.',
    mediaSubtitle: 'Tell us about your current marketing channels so we can AI-power them',
    mediaStep1: 'Step 1',
    mediaStep1Desc: 'Select the channels you currently use by clicking on them',
    mediaStep2: 'Step 2',
    mediaStep2Desc: 'Fill in the details for each active channel',
    mediaStep3: 'Step 3',
    mediaStep3Desc: 'Upload sample videos for AI reference, then save',
    mediaClickToActivate: 'Click to activate — we\'ll show you how AI transforms this channel',
    mediaAiWillDo: 'AI will:',
    mediaActiveChannels: 'active channels',
    mediaBrochures: 'Marketing Materials',
    mediaBrochuresDesc: 'Upload brochures, social media posts, email templates, or any marketing materials you currently use',
    uploadBrochures: 'Upload Marketing Materials',
    welcomeTitle: 'Welcome, {name}!',
    welcomeSubtitle: 'HOUSE AI Partner Onboarding Portal',
    welcomeDesc: 'This portal will help us understand your business so we can build the perfect AI platform for you. Complete the survey sections at your own pace — your progress is saved automatically.',
    unstoppable: 'AI + You = Unstoppable',
    feat1: 'AI Works 24/7', feat1d: 'Handles inquiries while you sleep',
    feat2: '10+ Languages', feat2d: "Speaks every buyer's language",
    feat3: '<2 Second Response', feat3d: 'Never miss a lead again',
    feat4: '80% Cost Reduction', feat4d: 'Scale without hiring',
    chatWelcome: "Hi {name}! I'm the HOUSE AI assistant. I can help you with:",
    chatHelp1: 'Questions about HOUSE AI platform',
    chatHelp2: 'Help filling out the survey',
    chatHelp3: 'Explain how AI transforms property sales',
    chatHelp4: 'Technical questions about integration',
    chatHelp5: 'Timeline and implementation details',
    chatSuggest1: 'What can AI do for me?',
    chatSuggest2: 'How long to set up?',
    chatSuggest3: 'Tell me about AI video tours',
  },
  zh: {
    partnerPortal: '合作夥伴入口',
    progress: '進度',
    aiAssistant: 'AI 助理',
    signIn: '登入',
    thankYouTitle: '感謝您，{name}！',
    thankYouSubtitle: '您的入門問卷已成功完成',
    thankYouLine1: 'HOUSE AI 團隊已收到您所有的問卷回覆與媒體資訊。我們正在為您的業務量身打造專屬的 AI 解決方案藍圖。',
    thankYouLine2: '我們會在接下來幾個工作日內主動與您聯繫下一步安排。有任何問題隨時與我們聯絡 — 我們很樂意服務您。',
    thankYouContact: '聯絡我們',
    thankYouSignature: '— 宇聯科技 HOUSE AI 團隊',
    sections: '調查區段',
    welcome: '歡迎',
    mediaAudit: '媒體審計',
    saveSection: '儲存此區段',
    saveMedia: '儲存媒體管道',
    savedOk: '儲存成功！',
    mediaOk: '媒體管道已儲存！',
    saveFail: '儲存失敗',
    uploaded: '已上傳！',
    uploadFail: '上傳失敗',
    fileDeleted: '檔案已刪除',
    deleteFail: '刪除失敗',
    clickUpload: '點擊或拖曳上傳',
    selectPlaceholder: '請選擇...',
    typePlaceholder: '請輸入您的回答...',
    previous: '← 上一個',
    nextSection: '下一個區段 →',
    completeAll: '完成問卷 ✓',
    completedMsg: '感謝您！問卷已完成。我們將審核您的回覆並盡快與您聯繫。',
    saveAndNext: '儲存並繼續 →',
    startSurvey: '開始填寫問卷 →',
    chatQuestion: '有問題嗎？與我們的 AI 助理對話',
    askAnything: '有什麼問題都可以問我...',
    connectionError: '連線錯誤，請重試。',
    loading: '正在載入 HOUSE AI 入口...',
    sampleVideos: 'AI 參考用範例影片',
    uploadVideos: '上傳範例銷售影片',
    toggleOn: '如果您有使用此管道，請開啟',
    mediaAlertTitle: '我們會將 AI 整合到您目前使用的每個管道，使其效率提升 10 倍。',
    mediaSubtitle: '告訴我們您目前的行銷管道，讓我們用 AI 強化它們',
    mediaStep1: '步驟 1',
    mediaStep1Desc: '點選您目前使用的管道',
    mediaStep2: '步驟 2',
    mediaStep2Desc: '填寫每個管道的詳細資訊',
    mediaStep3: '步驟 3',
    mediaStep3Desc: '上傳範例影片供 AI 參考，然後儲存',
    mediaClickToActivate: '點選啟用 — 我們會展示 AI 如何轉化此管道',
    mediaAiWillDo: 'AI 將會：',
    mediaActiveChannels: '個已啟用管道',
    mediaBrochures: '行銷素材',
    mediaBrochuresDesc: '上傳您目前使用的宣傳手冊、社群貼文、郵件範本或任何行銷素材',
    uploadBrochures: '上傳行銷素材',
    welcomeTitle: '歡迎，{name}！',
    welcomeSubtitle: 'HOUSE AI 合作夥伴入門平台',
    welcomeDesc: '此平台將幫助我們了解您的業務，以便為您打造完美的 AI 平台。您可以按照自己的節奏完成各區段的問卷——進度會自動儲存。',
    unstoppable: 'AI + 您 = 無敵',
    feat1: 'AI 全天候運作', feat1d: '在您休息時處理客戶詢問',
    feat2: '10+ 種語言', feat2d: '說每位買家的語言',
    feat3: '<2 秒回應', feat3d: '再也不會錯過任何線索',
    feat4: '降低 80% 成本', feat4d: '無需增聘即可擴展',
    chatWelcome: '嗨 {name}！我是 HOUSE AI 助理。我可以幫助您：',
    chatHelp1: '關於 HOUSE AI 平台的問題',
    chatHelp2: '協助填寫問卷',
    chatHelp3: '說明 AI 如何轉化房產銷售',
    chatHelp4: '技術整合問題',
    chatHelp5: '時程與實施細節',
    chatSuggest1: 'AI 能為我做什麼？',
    chatSuggest2: '設定需要多久？',
    chatSuggest3: '告訴我 AI 影片導覽',
  },
  th: {
    partnerPortal: 'พอร์ทัลพาร์ทเนอร์',
    progress: 'ความคืบหน้า',
    aiAssistant: 'ผู้ช่วย AI',
    signIn: 'เข้าสู่ระบบ',
    thankYouTitle: 'ขอบคุณ คุณ{name}!',
    thankYouSubtitle: 'แบบสอบถามออนบอร์ดดิ้งของคุณเสร็จสมบูรณ์แล้ว',
    thankYouLine1: 'ทีม HOUSE AI ได้รับคำตอบและข้อมูลช่องทางสื่อของคุณเรียบร้อยแล้ว ขณะนี้เรากำลังออกแบบแผน AI เฉพาะสำหรับธุรกิจของคุณ',
    thankYouLine2: 'เราจะติดต่อกลับภายในไม่กี่วันทำการพร้อมขั้นตอนถัดไป หากต้องการสอบถามเพิ่มเติม ติดต่อเราได้ตลอดเวลา',
    thankYouContact: 'ติดต่อเรา',
    thankYouSignature: '— ทีม Comboware HOUSE AI',
    sections: 'หัวข้อ',
    welcome: 'ยินดีต้อนรับ',
    mediaAudit: 'ตรวจสอบสื่อ',
    saveSection: 'บันทึกหัวข้อนี้',
    saveMedia: 'บันทึกช่องทางสื่อ',
    savedOk: 'บันทึกสำเร็จ!',
    mediaOk: 'บันทึกช่องทางสื่อแล้ว!',
    saveFail: 'บันทึกไม่สำเร็จ',
    uploaded: 'อัปโหลดแล้ว!',
    uploadFail: 'อัปโหลดไม่สำเร็จ',
    fileDeleted: 'ลบไฟล์แล้ว',
    deleteFail: 'ลบไม่สำเร็จ',
    clickUpload: 'คลิกหรือลากเพื่ออัปโหลด',
    selectPlaceholder: 'เลือก...',
    typePlaceholder: 'พิมพ์คำตอบของคุณ...',
    previous: '← ก่อนหน้า',
    nextSection: 'หัวข้อถัดไป →',
    completeAll: 'เสร็จสิ้นแบบสอบถาม ✓',
    completedMsg: 'ขอบคุณครับ/ค่ะ! แบบสอบถามเสร็จสมบูรณ์แล้ว เราจะตรวจสอบคำตอบของคุณและติดต่อกลับเร็วๆ นี้',
    saveAndNext: 'บันทึกและดำเนินการต่อ →',
    startSurvey: 'เริ่มแบบสอบถาม →',
    chatQuestion: 'มีคำถาม? แชทกับผู้ช่วย AI ของเรา',
    askAnything: 'ถามอะไรก็ได้...',
    connectionError: 'การเชื่อมต่อผิดพลาด กรุณาลองใหม่',
    loading: 'กำลังโหลด HOUSE AI Portal...',
    sampleVideos: 'วิดีโอตัวอย่างสำหรับ AI',
    uploadVideos: 'อัปโหลดวิดีโอตัวอย่างการขาย',
    toggleOn: 'เปิดหากคุณใช้ช่องทางนี้',
    mediaAlertTitle: 'เราจะผสาน AI เข้ากับทุกช่องทางที่คุณใช้อยู่ — ทำให้มีประสิทธิภาพมากขึ้น 10 เท่า',
    mediaSubtitle: 'บอกเราเกี่ยวกับช่องทางการตลาดปัจจุบันของคุณเพื่อให้เราเสริมพลัง AI',
    mediaStep1: 'ขั้นตอน 1',
    mediaStep1Desc: 'เลือกช่องทางที่คุณใช้อยู่โดยคลิกที่การ์ด',
    mediaStep2: 'ขั้นตอน 2',
    mediaStep2Desc: 'กรอกรายละเอียดสำหรับแต่ละช่องทาง',
    mediaStep3: 'ขั้นตอน 3',
    mediaStep3Desc: 'อัปโหลดวิดีโอตัวอย่างเพื่อให้ AI อ้างอิง แล้วบันทึก',
    mediaClickToActivate: 'คลิกเพื่อเปิดใช้งาน — เราจะแสดงว่า AI เปลี่ยนช่องทางนี้อย่างไร',
    mediaAiWillDo: 'AI จะ:',
    mediaActiveChannels: 'ช่องทางที่เปิดใช้งาน',
    mediaBrochures: 'สื่อการตลาด',
    mediaBrochuresDesc: 'อัปโหลดโบรชัวร์ โพสต์โซเชียล เทมเพลตอีเมล หรือสื่อการตลาดที่คุณใช้อยู่',
    uploadBrochures: 'อัปโหลดสื่อการตลาด',
    welcomeTitle: 'ยินดีต้อนรับ {name}!',
    welcomeSubtitle: 'พอร์ทัลออนบอร์ดดิ้งพาร์ทเนอร์ HOUSE AI',
    welcomeDesc: 'พอร์ทัลนี้จะช่วยให้เราเข้าใจธุรกิจของคุณเพื่อสร้างแพลตฟอร์ม AI ที่สมบูรณ์แบบสำหรับคุณ กรอกแบบสอบถามตามจังหวะของคุณ — ความคืบหน้าจะบันทึกอัตโนมัติ',
    unstoppable: 'AI + คุณ = เหนือชั้น',
    feat1: 'AI ทำงาน 24/7', feat1d: 'จัดการสอบถามขณะคุณหลับ',
    feat2: '10+ ภาษา', feat2d: 'พูดภาษาของผู้ซื้อทุกคน',
    feat3: 'ตอบ <2 วินาที', feat3d: 'ไม่พลาดลูกค้าอีกต่อไป',
    feat4: 'ลดต้นทุน 80%', feat4d: 'ขยายธุรกิจโดยไม่ต้องจ้างเพิ่ม',
    chatWelcome: 'สวัสดี {name}! ฉันคือผู้ช่วย HOUSE AI ฉันช่วยคุณได้เรื่อง:',
    chatHelp1: 'คำถามเกี่ยวกับแพลตฟอร์ม HOUSE AI',
    chatHelp2: 'ช่วยกรอกแบบสอบถาม',
    chatHelp3: 'อธิบายว่า AI เปลี่ยนการขายอสังหาฯ อย่างไร',
    chatHelp4: 'คำถามเทคนิคเกี่ยวกับการผสานระบบ',
    chatHelp5: 'ไทม์ไลน์และรายละเอียดการดำเนินงาน',
    chatSuggest1: 'AI ช่วยอะไรฉันได้บ้าง?',
    chatSuggest2: 'ตั้งค่าใช้เวลานานแค่ไหน?',
    chatSuggest3: 'เล่าเกี่ยวกับ AI Video Tour',
  },
};

const SECTIONS_I18N = {
  sales_process: {
    icon: <RocketOutlined />, color: '#27AE60',
    en: 'Sales Process', zh: '銷售流程', th: 'กระบวนการขาย',
  },
  customer_profiles: {
    icon: <TeamOutlined />, color: '#D4A03C',
    en: 'Customer Profiles', zh: '客戶側寫', th: 'โปรไฟล์ลูกค้า',
  },
  property_portfolio: {
    icon: <HomeOutlined />, color: '#00B4A0',
    en: 'Property Portfolio', zh: '房產組合', th: 'พอร์ตอสังหาริมทรัพย์',
  },
  team_structure: {
    icon: <ShopOutlined />, color: '#1A5CA8',
    en: 'Team Structure', zh: '團隊結構', th: 'โครงสร้างทีม',
  },
  communication_channels: {
    icon: <MessageOutlined />, color: '#E88D3C',
    en: 'Communication', zh: '溝通管道', th: 'การสื่อสาร',
  },
  faq_objections: {
    icon: <QuestionCircleOutlined />, color: '#9B59B6',
    en: 'FAQs & Objections', zh: 'FAQ & 異議', th: 'คำถามที่พบบ่อย',
  },
  current_tools: {
    icon: <ToolOutlined />, color: '#E84D3D',
    en: 'Current Tools', zh: '現有工具', th: 'เครื่องมือปัจจุบัน',
  },
  goals_painpoints: {
    icon: <AimOutlined />, color: '#2E86C1',
    en: 'Goals & Pain Points', zh: '目標與痛點', th: 'เป้าหมายและปัญหา',
  },
};

const SECTION_KEYS = Object.keys(SECTIONS_I18N);

const SURVEY_QUESTIONS_I18N = {
  sales_process: [
    { key: 'typical_flow', type: 'textarea',
      en: 'Walk us through a typical sale from first contact to closing — what happens at each stage?',
      zh: '請描述從首次接觸到成交的典型銷售流程——每個階段發生了什麼？',
      th: 'อธิบายกระบวนการขายทั่วไปตั้งแต่การติดต่อครั้งแรกจนถึงปิดการขาย — แต่ละขั้นตอนเป็นอย่างไร?' },
    { key: 'timeline', type: 'input',
      en: 'How long does the entire process usually take?',
      zh: '整個流程通常需要多長時間？',
      th: 'กระบวนการทั้งหมดใช้เวลานานเท่าไหร่?' },
    { key: 'conversion_inquiry_viewing', type: 'input',
      en: 'What % of inquiries convert to viewings?',
      zh: '詢問轉化為看房的比例是多少？',
      th: 'กี่ % ของการสอบถามที่เปลี่ยนเป็นการเยี่ยมชม?' },
    { key: 'conversion_viewing_offer', type: 'input',
      en: 'What % of viewings convert to offers?',
      zh: '看房轉化為報價的比例是多少？',
      th: 'กี่ % ของการเยี่ยมชมที่เปลี่ยนเป็นข้อเสนอ?' },
    { key: 'conversion_offer_deal', type: 'input',
      en: 'What % of offers convert to closed deals?',
      zh: '報價轉化為成交的比例是多少？',
      th: 'กี่ % ของข้อเสนอที่ปิดการขายได้?' },
    { key: 'bottleneck', type: 'textarea',
      en: 'Which step takes the longest or causes the most drop-off?',
      zh: '哪個步驟耗時最長或流失最多？',
      th: 'ขั้นตอนไหนใช้เวลานานที่สุดหรือทำให้ลูกค้าหายมากที่สุด?' },
    { key: 'buy_vs_rent', type: 'textarea',
      en: 'Is the process different for buyers vs renters? How?',
      zh: '買家與租客的流程是否不同？如何不同？',
      th: 'กระบวนการแตกต่างกันระหว่างผู้ซื้อกับผู้เช่าไหม? อย่างไร?' },
    { key: 'sales_scripts', type: 'upload',
      en: 'Upload any sales scripts, templates, or pitch documents you use',
      zh: '上傳您使用的銷售腳本、範本或推銷文件',
      th: 'อัปโหลดสคริปต์การขาย เทมเพลต หรือเอกสารนำเสนอที่คุณใช้' },
  ],
  customer_profiles: [
    { key: 'top_nationalities', type: 'textarea',
      en: 'Top 5 buyer nationalities and approximate % each',
      zh: '前 5 大買家國籍及其大約佔比',
      th: 'สัญชาติผู้ซื้อ 5 อันดับแรกและสัดส่วนโดยประมาณ' },
    { key: 'languages', type: 'input',
      en: 'What languages do your buyers communicate in?',
      zh: '您的買家使用什麼語言溝通？',
      th: 'ผู้ซื้อของคุณสื่อสารด้วยภาษาอะไรบ้าง?' },
    { key: 'age_profession', type: 'input',
      en: 'Age range and profession of typical buyers?',
      zh: '典型買家的年齡範圍和職業？',
      th: 'ช่วงอายุและอาชีพของผู้ซื้อทั่วไป?' },
    { key: 'buying_purpose', type: 'select',
      en: 'Primary purpose: Investment / Living / Retirement / Vacation?',
      zh: '主要目的：投資/自住/退休/度假？',
      th: 'วัตถุประสงค์หลัก: ลงทุน / อยู่อาศัย / เกษียณ / พักผ่อน?',
      options: ['Investment', 'Living', 'Retirement', 'Vacation', 'Mixed'] },
    { key: 'budget_range', type: 'textarea',
      en: 'Typical budget range for each buyer group',
      zh: '各買家群組的典型預算範圍',
      th: 'ช่วงงบประมาณทั่วไปของกลุ่มผู้ซื้อแต่ละกลุ่ม' },
    { key: 'buyer_location', type: 'select',
      en: 'Are buyers usually in Bangkok or overseas when first contact?',
      zh: '首次聯絡時買家通常在曼谷還是海外？',
      th: 'ผู้ซื้อมักอยู่ในกรุงเทพฯ หรือต่างประเทศเมื่อติดต่อครั้งแรก?',
      options: ['Mostly in Bangkok', 'Mostly overseas', 'About 50/50'] },
    { key: 'properties_viewed', type: 'input',
      en: 'How many properties do they typically view before deciding?',
      zh: '他們通常看幾處房產才做決定？',
      th: 'ปกติดูอสังหาฯ กี่แห่งก่อนตัดสินใจ?' },
    { key: 'decision_time', type: 'input',
      en: 'Average time from first inquiry to final decision?',
      zh: '從首次詢問到最終決定的平均時間？',
      th: 'เวลาเฉลี่ยจากการสอบถามครั้งแรกถึงการตัดสินใจ?' },
    { key: 'biggest_concerns', type: 'textarea',
      en: "What are buyers' biggest concerns or fears?",
      zh: '買家最大的顧慮或擔心是什麼？',
      th: 'ความกังวลหรือความกลัวที่ใหญ่ที่สุดของผู้ซื้อคืออะไร?' },
    { key: 'why_choose_you', type: 'textarea',
      en: 'What makes buyers choose YOU over other agents?',
      zh: '是什麼讓買家選擇您而非其他經紀人？',
      th: 'อะไรทำให้ผู้ซื้อเลือกคุณมากกว่าตัวแทนคนอื่น?' },
  ],
  property_portfolio: [
    { key: 'property_types', type: 'textarea',
      en: 'Property types (Condo, House, Villa, Commercial, etc.) & main focus',
      zh: '房產類型（公寓、別墅、商業等）及主要業務重點',
      th: 'ประเภทอสังหาฯ (คอนโด บ้าน วิลล่า พาณิชย์ ฯลฯ) และจุดเน้นหลัก' },
    { key: 'price_range', type: 'input',
      en: 'Lowest, highest, and most common price range',
      zh: '最低、最高及最常見的價格範圍',
      th: 'ช่วงราคาต่ำสุด สูงสุด และพบบ่อยที่สุด' },
    { key: 'locations', type: 'textarea',
      en: 'Bangkok areas you cover (e.g., Sukhumvit, Sathorn, Thonglor)',
      zh: '您涵蓋的曼谷區域（如 Sukhumvit、Sathorn、Thonglor）',
      th: 'พื้นที่ในกรุงเทพฯ ที่คุณดูแล (เช่น สุขุมวิท สาทร ทองหล่อ)' },
    { key: 'active_listings', type: 'input',
      en: 'How many active properties? How many new per month?',
      zh: '目前有多少活躍房源？每月新增多少？',
      th: 'มีอสังหาฯ ที่ขายอยู่กี่รายการ? เพิ่มใหม่กี่รายการต่อเดือน?' },
    { key: 'developers', type: 'textarea',
      en: 'Developer partnerships? Exclusive deals?',
      zh: '與開發商的合作關係？獨家代理？',
      th: 'ร่วมมือกับ developer ไหม? มี exclusive deal ไหม?' },
    { key: 'new_vs_resale', type: 'input',
      en: 'New construction vs resale %',
      zh: '新建 vs 二手比例',
      th: 'สัดส่วนโครงการใหม่ vs ขายต่อ' },
    { key: 'sale_vs_rent', type: 'input',
      en: 'For sale vs for rent %',
      zh: '出售 vs 出租比例',
      th: 'สัดส่วนขาย vs ให้เช่า' },
    { key: 'pricing_model', type: 'input',
      en: 'How do you earn? Commission %, fixed fee, or developer payment?',
      zh: '您如何賺取收入？佣金 %、固定費用、還是開發商付款？',
      th: 'คุณมีรายได้อย่างไร? ค่าคอมมิชชัน % ค่าธรรมเนียมคงที่ หรือ developer จ่าย?' },
    { key: 'property_data', type: 'upload',
      en: 'Upload sample property listing data (Excel/CSV/Photos)',
      zh: '上傳範例房產資料（Excel/CSV/照片）',
      th: 'อัปโหลดข้อมูลตัวอย่างรายการอสังหาฯ (Excel/CSV/รูปภาพ)' },
    { key: 'brochures', type: 'upload',
      en: 'Upload any property brochures or marketing materials',
      zh: '上傳任何房產手冊或行銷素材',
      th: 'อัปโหลดโบรชัวร์หรือสื่อการตลาด' },
  ],
  team_structure: [
    { key: 'total_members', type: 'input',
      en: 'Total team members?',
      zh: '團隊總人數？',
      th: 'สมาชิกในทีมทั้งหมดกี่คน?' },
    { key: 'sales_agents', type: 'textarea',
      en: 'Sales agents: How many? Languages? Daily tasks?',
      zh: '銷售人員：多少人？語言能力？日常工作？',
      th: 'ตัวแทนขาย: กี่คน? ภาษาอะไรบ้าง? งานประจำวัน?' },
    { key: 'admin_support', type: 'textarea',
      en: 'Admin/Support: Who handles inquiries? Schedules? Paperwork?',
      zh: '行政/支援：誰處理詢問？排程？文書？',
      th: 'แอดมิน/สนับสนุน: ใครจัดการสอบถาม? ตารางงาน? เอกสาร?' },
    { key: 'marketing_team', type: 'textarea',
      en: 'Marketing: Who creates content? Manages social? Runs ads?',
      zh: '行銷：誰製作內容？管理社群？投放廣告？',
      th: 'การตลาด: ใครสร้างคอนเทนต์? จัดการโซเชียล? ยิงแอด?' },
    { key: 'legal_finance', type: 'textarea',
      en: 'Legal/Finance: Contracts? External lawyers? Payments?',
      zh: '法務/財務：合約？外部律師？付款？',
      th: 'กฎหมาย/การเงิน: สัญญา? ทนายภายนอก? การชำระเงิน?' },
    { key: 'working_hours', type: 'input',
      en: 'Working hours? Different shifts?',
      zh: '工作時間？不同班次？',
      th: 'เวลาทำงาน? มีกะต่างๆ ไหม?' },
    { key: 'biggest_challenge', type: 'textarea',
      en: 'Biggest staffing challenge?',
      zh: '最大的人力挑戰？',
      th: 'ความท้าทายด้านบุคลากรที่ใหญ่ที่สุด?' },
    { key: 'low_value_tasks', type: 'textarea',
      en: 'Which tasks take the most time but add least value?',
      zh: '哪些工作耗時最多但附加價值最少？',
      th: 'งานไหนใช้เวลามากที่สุดแต่สร้างคุณค่าน้อยที่สุด?' },
  ],
  communication_channels: [
    { key: 'primary_channel', type: 'select',
      en: 'What is your #1 communication channel with buyers?',
      zh: '您與買家最主要的溝通管道是什麼？',
      th: 'ช่องทางการสื่อสารหลักกับผู้ซื้อคืออะไร?',
      options: ['LINE', 'WhatsApp', 'Facebook Messenger', 'Email', 'Phone', 'Website chat', 'Other'] },
    { key: 'response_time', type: 'select',
      en: 'Average response time to a new inquiry?',
      zh: '回覆新詢問的平均時間？',
      th: 'เวลาตอบกลับเฉลี่ยสำหรับการสอบถามใหม่?',
      options: ['Under 5 minutes', '5-30 minutes', '30 min - 1 hour', '1-4 hours', 'Same day', 'Next day'] },
    { key: 'after_hours', type: 'textarea',
      en: 'How do you handle inquiries outside business hours?',
      zh: '您如何處理非營業時間的詢問？',
      th: 'คุณจัดการสอบถามนอกเวลาทำงานอย่างไร?' },
    { key: 'lead_sources', type: 'textarea',
      en: 'Top 3 sources of new leads (ranked)',
      zh: '前 3 大新客戶來源（排名）',
      th: '3 แหล่งที่มาของลีดใหม่อันดับต้น (จัดอันดับ)' },
    { key: 'marketing_budget', type: 'input',
      en: 'Monthly marketing/advertising budget (approx)?',
      zh: '每月行銷/廣告預算（約）？',
      th: 'งบการตลาด/โฆษณาต่อเดือน (ประมาณ)?' },
    { key: 'content_frequency', type: 'input',
      en: 'How often do you post content? (daily/weekly/monthly)',
      zh: '您多久發佈一次內容？（每天/每週/每月）',
      th: 'คุณโพสต์คอนเทนต์บ่อยแค่ไหน? (ทุกวัน/ทุกสัปดาห์/ทุกเดือน)' },
  ],
  faq_objections: [
    { key: 'top_10_questions', type: 'textarea',
      en: 'Top 10 questions buyers ask (with your best answers)',
      zh: '買家最常問的 10 個問題（附您的最佳回答）',
      th: '10 คำถามที่ผู้ซื้อถามบ่อยที่สุด (พร้อมคำตอบที่ดีที่สุดของคุณ)' },
    { key: 'foreign_ownership', type: 'textarea',
      en: 'How do you explain foreign ownership rules in Thailand?',
      zh: '您如何解釋泰國的外國人持有權規定？',
      th: 'คุณอธิบายกฎการถือครองอสังหาฯ ของชาวต่างชาติในไทยอย่างไร?' },
    { key: 'total_costs', type: 'textarea',
      en: 'How do you explain total costs beyond purchase price?',
      zh: '您如何解釋購買價格以外的總費用？',
      th: 'คุณอธิบายค่าใช้จ่ายทั้งหมดนอกเหนือจากราคาซื้ออย่างไร?' },
    { key: 'common_objections', type: 'textarea',
      en: 'Most common objections and how you handle them',
      zh: '最常見的異議及您的處理方式',
      th: 'ข้อโต้แย้งที่พบบ่อยที่สุดและคุณจัดการอย่างไร' },
    { key: 'trust_concerns', type: 'textarea',
      en: 'How do you address trust/scam concerns from remote buyers?',
      zh: '您如何處理遠端買家的信任/詐騙疑慮？',
      th: 'คุณจัดการกับความกังวลเรื่องความน่าเชื่อถือ/การหลอกลวงจากผู้ซื้อระยะไกลอย่างไร?' },
    { key: 'deal_breakers', type: 'textarea',
      en: 'Most common reasons a deal falls through?',
      zh: '交易失敗最常見的原因？',
      th: 'สาเหตุที่พบบ่อยที่สุดที่ดีลล้มเหลว?' },
    { key: 'best_closing', type: 'textarea',
      en: 'Your best closing technique that works?',
      zh: '您最有效的成交技巧？',
      th: 'เทคนิคปิดการขายที่ดีที่สุดของคุณ?' },
    { key: 'faq_doc', type: 'upload',
      en: 'Upload FAQ document or sales scripts',
      zh: '上傳 FAQ 文件或銷售腳本',
      th: 'อัปโหลดเอกสาร FAQ หรือสคริปต์การขาย' },
  ],
  current_tools: [
    { key: 'crm', type: 'select',
      en: 'CRM/Contact management tool',
      zh: 'CRM/聯絡人管理工具',
      th: 'เครื่องมือ CRM/จัดการรายชื่อ',
      options: ['Excel/Sheets', 'Salesforce', 'HubSpot', 'LINE contacts', 'None', 'Other'] },
    { key: 'crm_detail', type: 'textarea',
      en: 'Details about your CRM/contact management',
      zh: '關於您的 CRM/聯絡人管理的細節',
      th: 'รายละเอียดเกี่ยวกับ CRM/การจัดการรายชื่อของคุณ' },
    { key: 'listing_system', type: 'textarea',
      en: 'How do you manage property inventory?',
      zh: '您如何管理房產庫存？',
      th: 'คุณจัดการสินค้าคงคลังอสังหาฯ อย่างไร?' },
    { key: 'photo_video', type: 'textarea',
      en: 'Photo/video tools and process',
      zh: '照片/影片工具與流程',
      th: 'เครื่องมือและกระบวนการถ่ายภาพ/วิดีโอ' },
    { key: 'website_url', type: 'input',
      en: 'Your website URL',
      zh: '您的網站網址',
      th: 'URL เว็บไซต์ของคุณ' },
    { key: 'website_platform', type: 'input',
      en: 'Website platform (WordPress, custom, etc.)',
      zh: '網站平台（WordPress、自建等）',
      th: 'แพลตฟอร์มเว็บไซต์ (WordPress, custom ฯลฯ)' },
    { key: 'analytics', type: 'textarea',
      en: 'Do you track leads, conversion, revenue? How?',
      zh: '您是否追蹤潛在客戶、轉換率、營收？如何追蹤？',
      th: 'คุณติดตามลีด การแปลง รายได้ไหม? อย่างไร?' },
    { key: 'data_export', type: 'select',
      en: 'Can you export your property list? (Excel, CSV)',
      zh: '您是否可以匯出房產清單？（Excel、CSV）',
      th: 'คุณสามารถ export รายการอสังหาฯ ได้ไหม? (Excel, CSV)',
      options: ['Yes - Excel', 'Yes - CSV', 'Yes - Other format', 'No - manual only', 'Not sure'] },
  ],
  goals_painpoints: [
    { key: 'revenue_target', type: 'input',
      en: 'Revenue target for next 12 months?',
      zh: '未來 12 個月的營收目標？',
      th: 'เป้าหมายรายได้ใน 12 เดือนข้างหน้า?' },
    { key: 'current_deals', type: 'input',
      en: 'How many deals/month do you close now?',
      zh: '您目前每月成交幾筆？',
      th: 'ตอนนี้คุณปิดดีลได้กี่รายการต่อเดือน?' },
    { key: 'target_deals', type: 'input',
      en: 'Target number of deals with AI?',
      zh: '使用 AI 後的目標成交數？',
      th: 'จำนวนดีลเป้าหมายเมื่อใช้ AI?' },
    { key: 'expand_plans', type: 'textarea',
      en: 'Plans to expand to new areas/markets?',
      zh: '擴展到新區域/市場的計劃？',
      th: 'แผนขยายไปพื้นที่/ตลาดใหม่?' },
    { key: 'biggest_waste', type: 'textarea',
      en: 'What wastes the most time daily?',
      zh: '每天什麼事最浪費時間？',
      th: 'อะไรเสียเวลามากที่สุดในแต่ละวัน?' },
    { key: 'lost_leads', type: 'textarea',
      en: 'Where do you lose the most leads?',
      zh: '您在哪裡流失最多潛在客戶？',
      th: 'คุณเสียลีดมากที่สุดตรงไหน?' },
    { key: 'competitor_advantage', type: 'textarea',
      en: 'What do competitors do better than you?',
      zh: '競爭對手在哪方面做得比您好？',
      th: 'คู่แข่งทำอะไรได้ดีกว่าคุณ?' },
    { key: 'ai_wishlist', type: 'textarea',
      en: 'If AI could do ONE thing for you, what would it be?',
      zh: '如果 AI 能為您做一件事，那會是什麼？',
      th: 'ถ้า AI ทำได้ 1 อย่างให้คุณ จะเป็นอะไร?' },
    { key: 'urgent_language', type: 'select',
      en: 'Which language support is most urgent?',
      zh: '哪種語言支援最迫切？',
      th: 'ภาษาไหนที่ต้องการสนับสนุนเร่งด่วนที่สุด?',
      options: ['Chinese (Mandarin)', 'Japanese', 'Korean', 'Russian', 'English', 'Other'] },
    { key: 'tenfold', type: 'textarea',
      en: 'What would 10x your business?',
      zh: '什麼能讓您的業務增長 10 倍？',
      th: 'อะไรจะทำให้ธุรกิจคุณโตขึ้น 10 เท่า?' },
  ],
};

const MEDIA_CHANNELS = [
  { key: 'facebook', icon: <FacebookOutlined />, label: 'Facebook', color: '#1877F2',
    fields: ['page_url', 'followers', 'ads_budget', 'post_frequency'],
    desc: { en: 'Facebook Page & Ads', zh: 'Facebook 粉專與廣告', th: 'เพจ Facebook และโฆษณา' },
    ai: { en: 'AI auto-posts listings, generates ad copy, responds to Messenger 24/7', zh: 'AI 自動發佈房源、生成廣告文案、Messenger 全天候回覆', th: 'AI โพสต์อัตโนมัติ สร้างข้อความโฆษณา ตอบ Messenger 24/7' },
  },
  { key: 'instagram', icon: <InstagramOutlined />, label: 'Instagram', color: '#E4405F',
    fields: ['profile_url', 'followers', 'content_type', 'post_frequency'],
    desc: { en: 'Instagram photos, reels & stories', zh: 'Instagram 照片、短片與限動', th: 'รูปภาพ Reels และ Stories บน Instagram' },
    ai: { en: 'AI creates property carousel posts, writes captions in 10+ languages', zh: 'AI 製作房產輪播貼文、以 10+ 語言撰寫說明', th: 'AI สร้างโพสต์แบบ carousel เขียน caption ใน 10+ ภาษา' },
  },
  { key: 'youtube', icon: <YoutubeOutlined />, label: 'YouTube', color: '#FF0000',
    fields: ['channel_url', 'subscribers', 'video_count', 'upload_frequency'],
    desc: { en: 'YouTube channel & video content', zh: 'YouTube 頻道與影片', th: 'ช่อง YouTube และวิดีโอ' },
    ai: { en: 'AI generates video tours with lip-synced narration in buyer\'s language', zh: 'AI 生成口型同步的多語言影片導覽', th: 'AI สร้างวิดีโอทัวร์พร้อม lip-sync ในภาษาผู้ซื้อ' },
  },
  { key: 'google_ads', icon: <GoogleOutlined />, label: 'Google Ads', color: '#4285F4',
    fields: ['account_status', 'monthly_budget', 'campaign_types'],
    desc: { en: 'Google search & display ads', zh: 'Google 搜尋與展示廣告', th: 'โฆษณา Google Search และ Display' },
    ai: { en: 'AI optimizes keywords, writes ad copy, manages bidding for max ROI', zh: 'AI 優化關鍵字、撰寫廣告文案、管理出價以最大化 ROI', th: 'AI เพิ่มประสิทธิภาพคีย์เวิร์ด เขียนโฆษณา จัดการ bid เพื่อ ROI สูงสุด' },
  },
  { key: 'line', icon: <MessageOutlined />, label: 'LINE', color: '#00B900',
    fields: ['account_type', 'friends_count', 'usage'],
    desc: { en: 'LINE Official Account', zh: 'LINE 官方帳號', th: 'บัญชี LINE Official' },
    ai: { en: 'AI chatbot handles LINE inquiries, sends personalized property alerts', zh: 'AI 聊天機器人處理 LINE 詢問、發送個人化房產通知', th: 'AI chatbot จัดการสอบถามทาง LINE ส่งแจ้งเตือนอสังหาฯ' },
  },
  { key: 'tiktok', icon: <PlayCircleOutlined />, label: 'TikTok', color: '#000000',
    fields: ['profile_url', 'followers', 'content_type'],
    desc: { en: 'TikTok short videos', zh: 'TikTok 短影片', th: 'วิดีโอสั้น TikTok' },
    ai: { en: 'AI generates trending property clips with multilingual voiceover', zh: 'AI 生成熱門房產短片搭配多語言旁白', th: 'AI สร้างคลิปอสังหาฯ พร้อมเสียงบรรยายหลายภาษา' },
  },
  { key: 'website', icon: <GlobalOutlined />, label: 'Website', color: '#1A5CA8',
    fields: ['url', 'monthly_visitors', 'platform'],
    desc: { en: 'Your business website', zh: '您的企業官網', th: 'เว็บไซต์ธุรกิจของคุณ' },
    ai: { en: 'AI-powered search, chatbot, auto-updated listings, SEO optimization', zh: 'AI 搜尋、聊天機器人、自動更新房源、SEO 優化', th: 'AI ค้นหา chatbot อัปเดตอัตโนมัติ เพิ่มประสิทธิภาพ SEO' },
  },
  { key: 'email_edm', icon: <MailOutlined />, label: 'EDM / Email', color: '#D4A03C',
    fields: ['platform', 'list_size', 'frequency', 'open_rate'],
    desc: { en: 'Email newsletters & campaigns', zh: '電子郵件與行銷活動', th: 'จดหมายข่าวและแคมเปญอีเมล' },
    ai: { en: 'AI writes personalized emails, A/B tests subject lines, auto-segments lists', zh: 'AI 撰寫個人化郵件、A/B 測試標題、自動分群', th: 'AI เขียนอีเมลส่วนตัว A/B test หัวเรื่อง แบ่งกลุ่มอัตโนมัติ' },
  },
  { key: 'blog', icon: <FileTextOutlined />, label: 'Blog', color: '#9B59B6',
    fields: ['url', 'post_frequency', 'topics'],
    desc: { en: 'Blog / content marketing', zh: '部落格/內容行銷', th: 'บล็อก / การตลาดเนื้อหา' },
    ai: { en: 'AI generates SEO-optimized property articles in multiple languages', zh: 'AI 生成多語言 SEO 優化房產文章', th: 'AI สร้างบทความอสังหาฯ ที่ปรับ SEO หลายภาษา' },
  },
  { key: 'property_portal', icon: <HomeOutlined />, label: 'Property Portals', color: '#00B4A0',
    fields: ['portals_used', 'best_performing'],
    desc: { en: 'DDproperty, Hipflat, etc.', zh: 'DDproperty、Hipflat 等', th: 'DDproperty, Hipflat ฯลฯ' },
    ai: { en: 'AI syncs listings across portals, optimizes descriptions per platform', zh: 'AI 跨平台同步房源、針對各平台優化描述', th: 'AI ซิงค์รายการข้ามพอร์ทัล เพิ่มประสิทธิภาพคำอธิบาย' },
  },
];

const FIELD_LABELS = {
  page_url:       { en: 'Page URL',        zh: '粉專網址',     th: 'URL เพจ' },
  profile_url:    { en: 'Profile URL',      zh: '個人頁網址',   th: 'URL โปรไฟล์' },
  channel_url:    { en: 'Channel URL',      zh: '頻道網址',     th: 'URL ช่อง' },
  followers:      { en: 'Followers',        zh: '粉絲數',       th: 'ผู้ติดตาม' },
  subscribers:    { en: 'Subscribers',      zh: '訂閱者',       th: 'ผู้ติดตาม' },
  ads_budget:     { en: 'Ads Budget',       zh: '廣告預算',     th: 'งบโฆษณา' },
  monthly_budget: { en: 'Monthly Budget',   zh: '月預算',       th: 'งบรายเดือน' },
  post_frequency: { en: 'Post Frequency',   zh: '發文頻率',     th: 'ความถี่โพสต์' },
  upload_frequency: { en: 'Upload Freq.',   zh: '上傳頻率',     th: 'ความถี่อัปโหลด' },
  content_type:   { en: 'Content Type',     zh: '內容類型',     th: 'ประเภทคอนเทนต์' },
  video_count:    { en: 'Video Count',      zh: '影片數',       th: 'จำนวนวิดีโอ' },
  account_status: { en: 'Account Status',   zh: '帳號狀態',     th: 'สถานะบัญชี' },
  campaign_types: { en: 'Campaign Types',   zh: '廣告類型',     th: 'ประเภทแคมเปญ' },
  account_type:   { en: 'Account Type',     zh: '帳號類型',     th: 'ประเภทบัญชี' },
  friends_count:  { en: 'Friends Count',    zh: '好友數',       th: 'จำนวนเพื่อน' },
  usage:          { en: 'Usage',            zh: '使用方式',     th: 'การใช้งาน' },
  url:            { en: 'URL',              zh: '網址',         th: 'URL' },
  monthly_visitors: { en: 'Monthly Visitors', zh: '月訪客',    th: 'ผู้เข้าชมรายเดือน' },
  platform:       { en: 'Platform',         zh: '平台',         th: 'แพลตฟอร์ม' },
  list_size:      { en: 'List Size',        zh: '名單數',       th: 'ขนาดรายชื่อ' },
  frequency:      { en: 'Frequency',        zh: '頻率',         th: 'ความถี่' },
  open_rate:      { en: 'Open Rate',        zh: '開信率',       th: 'อัตราเปิดอ่าน' },
  topics:         { en: 'Topics',           zh: '主題',         th: 'หัวข้อ' },
  portals_used:   { en: 'Portals Used',     zh: '使用的平台',   th: 'พอร์ทัลที่ใช้' },
  best_performing: { en: 'Best Performing', zh: '表現最佳',     th: 'ที่ดีที่สุด' },
};


// ── JJ Project collapsible sidebar menu ──────────────────────────────────────
const JJ_ITEMS = [
  { key: '/jj-dashboard', icon: '📊', zh: 'JJ 儀表板',  en: 'JJ Dashboard' },
  { key: '/jj-inbox',     icon: '📨', zh: 'JJ 收件匣',  en: 'JJ Inbox' },
  { key: '/jj-crm',       icon: '📋', zh: '案例管理',    en: 'Case Management' },
  { key: '/jj-keywords',  icon: '🔑', zh: '關鍵字設定',  en: 'Property Keywords' },
  { key: '/house-ai-rental', icon: '🏠', zh: '物件搜尋', en: 'Property Search' },
];

function JJProjectMenu({ uiConfig, lang }) {
  const jjCfg = uiConfig?.jjproject || {};
  // default expanded = true unless admin set expanded:false
  const [expanded, setExpanded] = React.useState(jjCfg.expanded !== false);

  // items config: null/undefined = all visible; obj = per-key bool
  const itemsCfg = jjCfg.items || {};
  const visibleItems = JJ_ITEMS.filter(it => itemsCfg[it.key] !== false);

  if (visibleItems.length === 0) return null;

  return (
    <div style={{ margin: '16px 0 0', borderTop: '1px solid #14335C', paddingTop: 12 }}>
      {/* Collapsible header */}
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          padding: '6px 16px 8px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          userSelect: 'none',
        }}
      >
        <Text style={{ color: '#D4A03C', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
          🏙️ JJ Bangkok
        </Text>
        <span style={{ color: '#D4A03C', fontSize: 14, transition: 'transform 0.2s', display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          ›
        </span>
      </div>

      {/* Submenu items */}
      {expanded && visibleItems.map(item => (
        <div
          key={item.key}
          onClick={() => { window.location.href = item.key; }}
          style={{
            padding: '9px 20px 9px 28px', cursor: 'pointer',
            color: '#aaa', fontSize: 13, transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: 8,
            borderLeft: '3px solid transparent',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.background = '#14335C';
            e.currentTarget.style.borderLeftColor = '#D4A03C';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = '#aaa';
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderLeftColor = 'transparent';
          }}
        >
          <span>{item.icon}</span>
          <span>{lang === 'zh' ? item.zh : item.en}</span>
        </div>
      ))}
    </div>
  );
}

export default function PartnerPortalPage() {
  const { token: PARTNER_TOKEN } = useParams();
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('partner_lang') || 'en'; } catch { return 'en'; }
  });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [partnerName, setPartnerName] = useState('');
  const [surveyData, setSurveyData] = useState({});
  const [uiConfig, setUiConfig] = useState({});
  const [mediaData, setMediaData] = useState({});
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatbotToken, setChatbotToken] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [activeSection, setActiveSection] = useState('welcome');
  const [saving, setSaving] = useState(false);
  const chatEndRef = useRef(null);

  const _t = UI[lang] || UI.en;
  const t = new Proxy(_t, {
    get: (obj, prop) => {
      const val = obj[prop];
      if (typeof val === 'string' && val.includes('{name}')) {
        return val.replace(/\{name\}/g, partnerName || 'Partner');
      }
      return val;
    },
  });

  const changeLang = (newLang) => {
    setLang(newLang);
    try { localStorage.setItem('partner_lang', newLang); } catch {}
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/partner/${PARTNER_TOKEN}`);
      if (res.status === 404) { setNotFound(true); setLoading(false); return; }
      const data = await res.json();
      setPartnerName(data.partner_name || '');
      setSurveyData(data.responses || {});
      setMediaData(data.media_channels || {});
      setFiles(data.files || []);
      setProgress(data.progress_pct || 0);
      setChatbotToken(data.chatbot_token || '');
      setUiConfig(data.ui_config || {});
    } catch (e) { console.error(e); setNotFound(true); }
    setLoading(false);
  }, [PARTNER_TOKEN]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const welcomeVisible = uiConfig?.welcome?.visible !== false;
  const isReadOnly = uiConfig?.readonly?.visible === true;

  // If admin hid welcome, jump partner straight to first survey section
  useEffect(() => {
    if (!welcomeVisible && activeSection === 'welcome') {
      setActiveSection(SECTION_KEYS[0] || 'media_audit');
    }
  }, [welcomeVisible, activeSection]);

  const allNavKeys = welcomeVisible
    ? ['welcome', ...SECTION_KEYS, 'media_audit']
    : [...SECTION_KEYS, 'media_audit'];

  const advanceToNext = () => {
    const idx = allNavKeys.indexOf(activeSection);
    if (idx < allNavKeys.length - 1) {
      setActiveSection(allNavKeys[idx + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const saveSectionData = async (sectionKey, responses, advance = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/partner/${PARTNER_TOKEN}/survey`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: sectionKey, responses }),
      });
      const data = await res.json();
      if (data.ok) {
        setProgress(data.progress_pct);
        message.success(t.savedOk);
        if (advance) advanceToNext();
      }
    } catch { message.error(t.saveFail); }
    setSaving(false);
  };

  const saveMediaData = async (channels, showComplete = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/partner/${PARTNER_TOKEN}/media`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channels }),
      });
      const data = await res.json();
      if (data.ok) {
        setProgress(data.progress_pct);
        if (showComplete) {
          Modal.success({ title: '🎉', content: t.completedMsg });
        } else {
          message.success(t.mediaOk);
        }
      }
    } catch { message.error(t.saveFail); }
    setSaving(false);
  };

  const handleUpload = async (file, section, questionKey) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(
        `${API_BASE}/partner/${PARTNER_TOKEN}/upload?section=${section}&question_key=${questionKey}`,
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      if (data.ok) {
        setFiles(prev => [...prev, data.file]);
        message.success(`${file.name} ${t.uploaded}`);
      }
    } catch { message.error(t.uploadFail); }
    return false;
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await fetch(`${API_BASE}/partner/${PARTNER_TOKEN}/file/${fileId}`, { method: 'DELETE' });
      setFiles(prev => prev.filter(f => f.id !== fileId));
      message.success(t.fileDeleted);
    } catch { message.error(t.deleteFail); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    const updatedMessages = [...chatMessages, { role: 'user', content: userMsg }];
    setChatMessages(updatedMessages);
    setChatLoading(true);
    try {
      const res = await fetch(`${API_BASE}/partner/${PARTNER_TOKEN}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          conversation_history: updatedMessages.slice(-10),
        }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'Sorry, I could not process that.' }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: t.connectionError }]);
    }
    setChatLoading(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1F3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ color: '#D4A03C', marginTop: 16, fontSize: 18 }}>{_t.loading}</div>
        </div>
      </div>
    );
  }

  // Admin disabled portal AFTER partner completed onboarding — show warm thank-you screen.
  // (We have partnerName loaded from the API even when portal is hidden.)
  if (uiConfig?.portal?.visible === false) {
    const interpolate = (s) => (s || '').replace('{name}', partnerName || 'Partner');
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A1F3F 0%, #14335C 50%, #0A1F3F 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 20px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative gradient orbs */}
        <div style={{
          position: 'absolute', top: '-15%', left: '-10%', width: 480, height: 480,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,160,60,0.18) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-20%', right: '-15%', width: 560, height: 560,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(39,174,96,0.12) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          textAlign: 'center', maxWidth: 640, padding: '48px 36px',
          background: 'rgba(20, 51, 92, 0.55)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212,160,60,0.35)',
          borderRadius: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04)',
          position: 'relative', zIndex: 1,
        }}>
          {/* Gold gradient check badge */}
          <div style={{
            width: 96, height: 96, borderRadius: '50%', margin: '0 auto 24px',
            background: 'linear-gradient(135deg, #F0C75E 0%, #D4A03C 50%, #B8860B 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 32px rgba(212,160,60,0.45), 0 0 0 6px rgba(212,160,60,0.12)',
          }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#0A1F3F' }} />
          </div>

          <Title level={1} style={{
            color: '#fff', margin: 0, marginBottom: 8,
            fontSize: 36, fontWeight: 800, letterSpacing: -0.5,
          }}>
            {interpolate(t.thankYouTitle)}
          </Title>

          <Text style={{
            color: '#D4A03C', fontSize: 16, fontWeight: 600, letterSpacing: 1,
            display: 'block', marginBottom: 28,
          }}>
            {t.thankYouSubtitle}
          </Text>

          <Paragraph style={{ color: '#E8ECF1', fontSize: 16, lineHeight: 1.75, marginBottom: 14 }}>
            {t.thankYouLine1}
          </Paragraph>
          <Paragraph style={{ color: '#C7D0DC', fontSize: 15, lineHeight: 1.75, marginBottom: 36 }}>
            {t.thankYouLine2}
          </Paragraph>

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            paddingTop: 24, borderTop: '1px solid rgba(212,160,60,0.2)',
          }}>
            <Space size={8} align="center">
              <MailOutlined style={{ color: '#D4A03C', fontSize: 16 }} />
              <Text style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>{t.thankYouContact}</Text>
            </Space>
            <Text style={{ color: '#D4A03C', fontSize: 14 }}>ai@comboware.ai</Text>
            <Text style={{ color: '#777', fontSize: 12, marginTop: 12, letterSpacing: 1 }}>
              {t.thankYouSignature}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1F3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 500, padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
          <Title level={2} style={{ color: '#fff' }}>Portal Not Found</Title>
          <Paragraph style={{ color: '#999', fontSize: 16 }}>
            This partner portal link is invalid or has been removed.<br />
            Please check the URL or contact your Comboware representative.
          </Paragraph>
          <Text style={{ color: '#555', fontSize: 13 }}>ai@comboware.ai</Text>
        </div>
      </div>
    );
  }

  const sectionFiles = (section, qKey) => files.filter(f => f.section === section && f.question_key === qKey);

  const getFieldLabel = (fieldKey) => {
    const fl = FIELD_LABELS[fieldKey];
    return fl ? (fl[lang] || fl.en) : fieldKey.replace(/_/g, ' ');
  };

  const renderQuestion = (sectionKey, q) => {
    const val = surveyData[sectionKey]?.[q.key] || '';
    const label = q[lang] || q.en;
    const updateLocal = (v) => {
      setSurveyData(prev => ({
        ...prev,
        [sectionKey]: { ...prev[sectionKey], [q.key]: v },
      }));
    };

    if (q.type === 'upload') {
      const sFiles = sectionFiles(sectionKey, q.key);
      return (
        <div key={q.key} style={{ marginBottom: 20 }}>
          <Text style={{ color: '#E8ECF1', display: 'block', marginBottom: 8, fontSize: 14 }}>{label}</Text>
          <Upload
            beforeUpload={(file) => handleUpload(file, sectionKey, q.key)}
            showUploadList={false}
            multiple
            disabled={isReadOnly}
          >
            <Button icon={<CloudUploadOutlined />} disabled={isReadOnly} style={{ background: '#14335C', color: '#D4A03C', border: '1px dashed #D4A03C' }}>
              {t.clickUpload}
            </Button>
          </Upload>
          {sFiles.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {sFiles.map(f => (
                <Tag key={f.id} closable onClose={() => handleDeleteFile(f.id)}
                  style={{ marginBottom: 4, background: '#14335C', color: '#E8ECF1', border: '1px solid #1A5CA8' }}>
                  <PaperClipOutlined /> {f.original_filename}
                </Tag>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (q.type === 'select') {
      return (
        <div key={q.key} style={{ marginBottom: 20 }}>
          <Text style={{ color: '#E8ECF1', display: 'block', marginBottom: 8, fontSize: 14 }}>{label}</Text>
          <Select value={val || undefined} onChange={updateLocal} placeholder={t.selectPlaceholder}
            style={{ width: '100%' }} size="large"
            dropdownStyle={{ background: '#14335C' }}
            disabled={isReadOnly}
          >
            {(q.options || []).map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
          </Select>
        </div>
      );
    }

    if (q.type === 'textarea') {
      return (
        <div key={q.key} style={{ marginBottom: 20 }}>
          <Text style={{ color: '#E8ECF1', display: 'block', marginBottom: 8, fontSize: 14 }}>{label}</Text>
          <TextArea rows={3} value={val} onChange={e => updateLocal(e.target.value)}
            placeholder={t.typePlaceholder} disabled={isReadOnly}
            style={{ background: '#0D2847', color: '#fff', border: '1px solid #1A5CA8', borderRadius: 8 }} />
        </div>
      );
    }

    return (
      <div key={q.key} style={{ marginBottom: 20 }}>
        <Text style={{ color: '#E8ECF1', display: 'block', marginBottom: 8, fontSize: 14 }}>{label}</Text>
        <Input value={val} onChange={e => updateLocal(e.target.value)}
          placeholder={t.typePlaceholder} disabled={isReadOnly}
          size="large"
          style={{ background: '#0D2847', color: '#fff', border: '1px solid #1A5CA8', borderRadius: 8 }} />
      </div>
    );
  };

  const renderSurveySection = (secKey) => {
    const sec = SECTIONS_I18N[secKey];
    const questions = SURVEY_QUESTIONS_I18N[secKey] || [];
    const title = sec[lang] || sec.en;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ fontSize: 28, color: sec.color }}>{sec.icon}</div>
          <div>
            <Title level={4} style={{ color: '#fff', margin: 0 }}>{title}</Title>
            {lang === 'en' && <Text style={{ color: '#999' }}>{sec.zh}</Text>}
            {lang === 'zh' && <Text style={{ color: '#999' }}>{sec.en}</Text>}
            {lang === 'th' && <Text style={{ color: '#999' }}>{sec.en}</Text>}
          </div>
        </div>
        {questions.map(q => renderQuestion(secKey, q))}
        {isReadOnly ? (
          <Alert message="Read-only — survey responses cannot be edited" type="info" showIcon
            style={{ background: '#0D2240', border: '1px solid #1A5CA8', color: '#aaa', marginTop: 8 }} />
        ) : (
          <Space style={{ width: '100%', marginTop: 8 }} direction="vertical">
            <Button type="primary" icon={<SaveOutlined />} loading={saving}
              onClick={() => saveSectionData(secKey, surveyData[secKey] || {}, true)}
              style={{ background: `linear-gradient(135deg, ${sec.color}, #14335C)`, border: 'none', height: 44, fontSize: 15 }}
              block>
              {t.saveAndNext}
            </Button>
          </Space>
        )}
      </div>
    );
  };

  const activeChannelCount = MEDIA_CHANNELS.filter(ch => (mediaData[ch.key] || {}).active).length;

  const toggleChannel = (chKey) => {
    setMediaData(prev => ({
      ...prev,
      [chKey]: { ...prev[chKey], active: !(prev[chKey]?.active) },
    }));
  };

  const renderMediaAudit = () => (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: 'linear-gradient(135deg, #D4A03C, #B8860B)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ThunderboltOutlined style={{ fontSize: 24, color: '#fff' }} />
        </div>
        <div>
          <Title level={4} style={{ color: '#fff', margin: 0 }}>{t.mediaAudit}</Title>
          <Text style={{ color: '#999' }}>{t.mediaSubtitle}</Text>
        </div>
      </div>

      {/* Step-by-step guide */}
      <div style={{
        background: 'linear-gradient(135deg, #14335C, #0D2847)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 24,
        border: '1px solid #1A5CA8',
      }}>
        <Row gutter={16}>
          {[
            { num: t.mediaStep1, desc: t.mediaStep1Desc, icon: <CheckCircleOutlined />, color: '#27AE60' },
            { num: t.mediaStep2, desc: t.mediaStep2Desc, icon: <FileTextOutlined />, color: '#D4A03C' },
            { num: t.mediaStep3, desc: t.mediaStep3Desc, icon: <CloudUploadOutlined />, color: '#1890ff' },
          ].map((step, i) => (
            <Col xs={24} sm={8} key={i}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: `${step.color}22`, color: step.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>
                  {step.icon}
                </div>
                <div>
                  <Text style={{ color: step.color, fontWeight: 700, fontSize: 13, display: 'block' }}>{step.num}</Text>
                  <Text style={{ color: '#E8ECF1', fontSize: 12 }}>{step.desc}</Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Active count badge */}
      {activeChannelCount > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Tag color="#D4A03C" style={{ borderRadius: 20, padding: '2px 14px', fontSize: 13, fontWeight: 600 }}>
            <ThunderboltOutlined /> {activeChannelCount} {t.mediaActiveChannels}
          </Tag>
        </div>
      )}

      {/* Channel cards */}
      <Row gutter={[16, 16]}>
        {MEDIA_CHANNELS.map(ch => {
          const chData = mediaData[ch.key] || {};
          const isActive = chData.active;
          const chDesc = ch.desc?.[lang] || ch.desc?.en || '';
          const chAi = ch.ai?.[lang] || ch.ai?.en || '';
          return (
            <Col xs={24} sm={12} lg={8} key={ch.key}>
              <div
                style={{
                  background: isActive ? '#14335C' : '#0D2240',
                  border: isActive ? `2px solid ${ch.color}` : '1px solid #1A3050',
                  borderRadius: 14,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: isReadOnly ? 'default' : (isActive ? 'default' : 'pointer'),
                }}
                onClick={(!isActive && !isReadOnly) ? () => toggleChannel(ch.key) : undefined}
              >
                {/* Card header — always clickable to toggle */}
                <div
                  style={{
                    padding: '14px 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: isReadOnly ? 'default' : 'pointer',
                    background: isActive ? `${ch.color}15` : 'transparent',
                    borderBottom: isActive ? `1px solid ${ch.color}33` : 'none',
                  }}
                  onClick={(e) => { if (!isReadOnly) { e.stopPropagation(); toggleChannel(ch.key); } }}
                >
                  <Space size={10}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: isActive ? ch.color : '#14335C',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s',
                    }}>
                      <span style={{ color: isActive ? '#fff' : ch.color, fontSize: 18 }}>{ch.icon}</span>
                    </div>
                    <div>
                      <Text style={{ color: '#fff', fontWeight: 600, display: 'block', fontSize: 14 }}>{ch.label}</Text>
                      <Text style={{ color: '#888', fontSize: 11 }}>{chDesc}</Text>
                    </div>
                  </Space>
                  <Switch size="small" checked={isActive} disabled={isReadOnly}
                    onChange={() => !isReadOnly && toggleChannel(ch.key)}
                    onClick={(_, e) => e.stopPropagation()}
                  />
                </div>

                {/* Inactive state */}
                {!isActive && (
                  <div style={{ padding: '12px 16px 16px', textAlign: 'center' }}>
                    <Text style={{ color: '#555', fontSize: 12 }}>{t.mediaClickToActivate}</Text>
                  </div>
                )}

                {/* Active state — AI benefit + fields */}
                {isActive && (
                  <div style={{ padding: '12px 16px 16px' }}>
                    {/* AI transformation preview */}
                    <div style={{
                      background: '#0A1F3F', borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                      border: `1px solid ${ch.color}33`,
                    }}>
                      <Text style={{ color: ch.color, fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                        <RobotOutlined /> {t.mediaAiWillDo}
                      </Text>
                      <Text style={{ color: '#E8ECF1', fontSize: 12, lineHeight: 1.5 }}>{chAi}</Text>
                    </div>

                    {/* Input fields */}
                    {ch.fields.map(f => (
                      <div key={f} style={{ marginBottom: 10 }}>
                        <Text style={{ color: '#999', fontSize: 11, display: 'block', marginBottom: 3 }}>{getFieldLabel(f)}</Text>
                        <Input size="small" value={chData[f] || ''}
                          onChange={e => setMediaData(prev => ({
                            ...prev,
                            [ch.key]: { ...prev[ch.key], [f]: e.target.value },
                          }))}
                          style={{ background: '#0A1F3F', color: '#fff', border: '1px solid #1A5CA8', borderRadius: 6 }}
                          placeholder={`${getFieldLabel(f)}...`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          );
        })}
      </Row>

      {/* Upload sections */}
      <div style={{
        marginTop: 24, background: '#14335C', borderRadius: 14, padding: 20,
        border: '1px solid #1A5CA8',
      }}>
        <Row gutter={[24, 20]}>
          {/* Sample videos */}
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <VideoCameraOutlined style={{ color: '#D4A03C', fontSize: 18 }} />
              <Text style={{ color: '#D4A03C', fontWeight: 700, fontSize: 14 }}>{t.sampleVideos}</Text>
            </div>
            <Upload beforeUpload={(f) => handleUpload(f, 'media_videos', 'sample_videos')}
              showUploadList={false} multiple accept="video/*">
              <Button icon={<CloudUploadOutlined />} size="large" block
                style={{ background: '#0D2847', color: '#D4A03C', border: '1px dashed #D4A03C', borderRadius: 10, height: 48 }}>
                {t.uploadVideos}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, minHeight: 24 }}>
              {sectionFiles('media_videos', 'sample_videos').map(f => (
                <Tag key={f.id} closable onClose={() => handleDeleteFile(f.id)}
                  style={{ marginBottom: 4, background: '#0D2847', color: '#E8ECF1', border: '1px solid #1A5CA8' }}>
                  <VideoCameraOutlined /> {f.original_filename}
                </Tag>
              ))}
            </div>
          </Col>

          {/* Marketing materials */}
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />
              <Text style={{ color: '#1890ff', fontWeight: 700, fontSize: 14 }}>{t.mediaBrochures}</Text>
            </div>
            <Text style={{ color: '#999', fontSize: 11, display: 'block', marginBottom: 8 }}>{t.mediaBrochuresDesc}</Text>
            <Upload beforeUpload={(f) => handleUpload(f, 'media_brochures', 'brochures')}
              showUploadList={false} multiple>
              <Button icon={<CloudUploadOutlined />} size="large" block
                style={{ background: '#0D2847', color: '#1890ff', border: '1px dashed #1890ff', borderRadius: 10, height: 48 }}>
                {t.uploadBrochures}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, minHeight: 24 }}>
              {sectionFiles('media_brochures', 'brochures').map(f => (
                <Tag key={f.id} closable onClose={() => handleDeleteFile(f.id)}
                  style={{ marginBottom: 4, background: '#0D2847', color: '#E8ECF1', border: '1px solid #1A5CA8' }}>
                  <PaperClipOutlined /> {f.original_filename}
                </Tag>
              ))}
            </div>
          </Col>
        </Row>
      </div>

      {/* Save button */}
      <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={isReadOnly}
        onClick={() => saveMediaData(mediaData, false)} size="large"
        style={{
          background: isReadOnly ? '#14335C' : 'linear-gradient(135deg, #D4A03C, #B8860B)',
          border: 'none', height: 50, fontSize: 16, marginTop: 20, borderRadius: 12,
          fontWeight: 600,
        }}
        block>
        {isReadOnly ? 'Read-only' : t.saveMedia}
      </Button>
    </div>
  );

  const renderWelcome = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🏠</div>
      <Title level={2} style={{ color: '#fff', margin: 0 }}>{t.welcomeTitle}</Title>
      <Title level={4} style={{ color: '#D4A03C', fontWeight: 400, marginTop: 8 }}>
        {t.welcomeSubtitle}
      </Title>
      <Paragraph style={{ color: '#E8ECF1', fontSize: 16, maxWidth: 700, margin: '20px auto' }}>
        {t.welcomeDesc}
      </Paragraph>
      <div style={{
        background: 'linear-gradient(135deg, #14335C, #0D2847)',
        borderRadius: 16, padding: 24, maxWidth: 800, margin: '30px auto',
        border: '1px solid #1A5CA8',
      }}>
        <Title level={4} style={{ color: '#D4A03C' }}>{t.unstoppable}</Title>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {[
            { icon: <RobotOutlined />, title: t.feat1, desc: t.feat1d },
            { icon: <GlobalOutlined />, title: t.feat2, desc: t.feat2d },
            { icon: <ThunderboltOutlined />, title: t.feat3, desc: t.feat3d },
            { icon: <StarOutlined />, title: t.feat4, desc: t.feat4d },
          ].map((item, i) => (
            <Col xs={12} sm={6} key={i}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, color: '#D4A03C' }}>{item.icon}</div>
                <Text style={{ color: '#fff', fontWeight: 600, display: 'block', marginTop: 8 }}>{item.title}</Text>
                <Text style={{ color: '#999', fontSize: 12 }}>{item.desc}</Text>
              </div>
            </Col>
          ))}
        </Row>
      </div>
      <div style={{ marginTop: 30 }}>
        <Button type="primary" size="large" icon={<RocketOutlined />}
          onClick={() => setActiveSection('sales_process')}
          style={{ height: 52, fontSize: 18, background: 'linear-gradient(135deg, #D4A03C, #B8860B)', border: 'none', paddingInline: 40 }}>
          {t.startSurvey}
        </Button>
      </div>
      <div style={{ marginTop: 16 }}>
        <Button type="text" icon={<MessageOutlined />} onClick={() => setChatOpen(true)}
          style={{ color: '#D4A03C', fontSize: 15 }}>
          {t.chatQuestion}
        </Button>
      </div>
    </div>
  );

  const navItems = [
    ...(welcomeVisible ? [{ key: 'welcome', label: `🏠 ${t.welcome}`, icon: null }] : []),
    ...SECTION_KEYS.map(k => {
      const s = SECTIONS_I18N[k];
      return {
        key: k,
        label: (
          <Space size={4}>
            <span style={{ color: s.color }}>{s.icon}</span>
            <span>{s[lang] || s.en}</span>
            {surveyData[k] && Object.values(surveyData[k]).some(v => v) && (
              <CheckCircleOutlined style={{ color: '#27AE60', fontSize: 12 }} />
            )}
          </Space>
        ),
      };
    }),
    { key: 'media_audit', label: <Space><ThunderboltOutlined style={{ color: '#D4A03C' }} /> {t.mediaAudit}</Space> },
  ];

  const langSwitcher = (
    <Space size={4}>
      {LANG_OPTIONS.map(lo => (
        <Button key={lo.key} size="small" type={lang === lo.key ? 'primary' : 'text'}
          onClick={() => changeLang(lo.key)}
          style={{
            background: lang === lo.key ? '#D4A03C' : 'transparent',
            color: lang === lo.key ? '#0A1F3F' : '#999',
            border: lang === lo.key ? 'none' : '1px solid #14335C',
            borderRadius: 6, fontWeight: 600, fontSize: 12, minWidth: 50,
          }}>
          {lo.flag} {lo.label}
        </Button>
      ))}
    </Space>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0A1F3F' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0A1F3F, #14335C)',
        borderBottom: '2px solid #D4A03C',
        padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Space size={12}>
          <span style={{ fontSize: 28 }}>🏠</span>
          <div>
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>HOUSE AI</Text>
            <Text style={{ color: '#D4A03C', fontSize: 12, display: 'block', lineHeight: 1 }}>{t.partnerPortal}</Text>
          </div>
        </Space>
        <Space size={16}>
          {langSwitcher}
          <div style={{ textAlign: 'right' }}>
            <Text style={{ color: '#999', fontSize: 11, display: 'block' }}>{t.progress}</Text>
            <Progress percent={Math.round(progress)} size="small" strokeColor="#D4A03C"
              trailColor="#14335C" style={{ width: 120, margin: 0 }} />
          </div>
          {uiConfig?.login?.visible !== false && (
            <Button
              size="small"
              icon={<UserOutlined />}
              onClick={() => window.open('/login', '_blank', 'noopener,noreferrer')}
              style={{
                background: 'rgba(212,160,60,0.15)',
                border: '1px solid #D4A03C',
                color: '#D4A03C',
                fontWeight: 600,
              }}>
              {t.signIn}
            </Button>
          )}
          <Button type="primary" icon={<MessageOutlined />} onClick={() => setChatOpen(true)}
            style={{ background: '#D4A03C', border: 'none', color: '#0A1F3F', fontWeight: 600 }}>
            {t.aiAssistant}
          </Button>
        </Space>
      </div>

      {/* Main Layout */}
      <div style={{ display: 'flex', maxWidth: 1400, margin: '0 auto' }}>
        {/* Sidebar Nav */}
        <div style={{
          width: 260, minHeight: 'calc(100vh - 60px)', background: '#0D2240',
          borderRight: '1px solid #14335C', padding: '16px 0',
          position: 'sticky', top: 60, height: 'calc(100vh - 60px)', overflowY: 'auto',
        }}>
          <div style={{ padding: '0 16px 12px' }}>
            <Text style={{ color: '#999', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{t.sections}</Text>
          </div>
          {navItems.map(item => (
            <div key={item.key}
              onClick={() => setActiveSection(item.key)}
              style={{
                padding: '10px 20px', cursor: 'pointer',
                background: activeSection === item.key ? '#14335C' : 'transparent',
                borderLeft: activeSection === item.key ? '3px solid #D4A03C' : '3px solid transparent',
                color: activeSection === item.key ? '#fff' : '#999',
                fontSize: 13, transition: 'all 0.2s',
              }}
            >
              {item.label}
            </div>
          ))}

          {/* JJ Project expandable menu */}
          {uiConfig?.jjproject?.visible !== false && (
            <JJProjectMenu uiConfig={uiConfig} lang={lang} />
          )}

          <div style={{ padding: 16, marginTop: 16, borderTop: '1px solid #14335C' }}>
            <Text style={{ color: '#666', fontSize: 11, display: 'block', marginBottom: 8 }}>Comboware, Inc.</Text>
            <Text style={{ color: '#555', fontSize: 10 }}>Silicon Valley, CA, USA</Text>
            <br />
            <Text style={{ color: '#555', fontSize: 10 }}>ai@comboware.ai</Text>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, padding: '24px 32px', maxWidth: 900 }}>
          {activeSection === 'welcome' && renderWelcome()}
          {activeSection === 'media_audit' && renderMediaAudit()}
          {SECTION_KEYS.map(k => activeSection === k && (
            <div key={k}>{renderSurveySection(k)}</div>
          ))}

          {activeSection !== 'welcome' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #14335C' }}>
              <Button onClick={() => {
                const idx = allNavKeys.indexOf(activeSection);
                if (idx > 0) { setActiveSection(allNavKeys[idx - 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); }
              }} style={{ background: '#14335C', color: '#E8ECF1', border: 'none' }}>
                {t.previous}
              </Button>
              {activeSection === 'media_audit' ? (
                <Button type="primary" loading={saving} disabled={isReadOnly} onClick={() => saveMediaData(mediaData, true)}
                  style={{ background: isReadOnly ? '#14335C' : 'linear-gradient(135deg, #27AE60, #1E8449)', border: 'none', color: '#fff', fontWeight: 600 }}>
                  {isReadOnly ? 'Read-only' : t.completeAll}
                </Button>
              ) : (
                <Button type="primary" onClick={() => {
                  const idx = allNavKeys.indexOf(activeSection);
                  if (idx < allNavKeys.length - 1) { setActiveSection(allNavKeys[idx + 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                }} style={{ background: '#D4A03C', border: 'none', color: '#0A1F3F', fontWeight: 600 }}>
                  {t.nextSection}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Drawer */}
      <Drawer
        title={
          <Space>
            <RobotOutlined style={{ color: '#D4A03C' }} />
            <span style={{ color: '#fff' }}>{t.aiAssistant}</span>
          </Space>
        }
        placement="right" width={420}
        open={chatOpen} onClose={() => setChatOpen(false)}
        styles={{
          header: { background: '#0A1F3F', borderBottom: '1px solid #14335C' },
          body: { background: '#0D2240', padding: 0, display: 'flex', flexDirection: 'column' },
        }}
        closeIcon={<CloseOutlined style={{ color: '#999' }} />}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {chatMessages.length === 0 && (
            <div style={{
              background: '#14335C', borderRadius: 12, padding: 16, marginBottom: 12,
              border: '1px solid #1A5CA8',
            }}>
              <Space align="start">
                <Avatar style={{ background: '#D4A03C', color: '#0A1F3F' }} icon={<RobotOutlined />} />
                <div>
                  <Text style={{ color: '#fff', fontSize: 14 }}>
                    {t.chatWelcome}
                  </Text>
                  <ul style={{ color: '#E8ECF1', fontSize: 13, marginTop: 8, paddingLeft: 20 }}>
                    <li>{t.chatHelp1}</li>
                    <li>{t.chatHelp2}</li>
                    <li>{t.chatHelp3}</li>
                    <li>{t.chatHelp4}</li>
                    <li>{t.chatHelp5}</li>
                  </ul>
                  <div style={{ marginTop: 12 }}>
                    {[t.chatSuggest1, t.chatSuggest2, t.chatSuggest3].map(q => (
                      <Tag key={q} style={{ cursor: 'pointer', background: '#0A1F3F', color: '#D4A03C', border: '1px solid #D4A03C', marginBottom: 4 }}
                        onClick={() => { setChatInput(q); }}>
                        {q}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Space>
            </div>
          )}
          {chatMessages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}>
              <div style={{
                maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
                background: msg.role === 'user' ? '#D4A03C' : '#14335C',
                color: msg.role === 'user' ? '#0A1F3F' : '#E8ECF1',
                fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.5,
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Avatar size="small" style={{ background: '#D4A03C', color: '#0A1F3F' }} icon={<RobotOutlined />} />
              <div style={{ background: '#14335C', borderRadius: 12, padding: '10px 14px' }}>
                <Spin size="small" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #14335C', background: '#0A1F3F' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onPressEnter={sendChat} placeholder={t.askAnything}
              style={{ background: '#0D2240', color: '#fff', border: '1px solid #1A5CA8' }} />
            <Button type="primary" icon={<SendOutlined />} onClick={sendChat} loading={chatLoading}
              style={{ background: '#D4A03C', border: 'none', color: '#0A1F3F' }} />
          </Space.Compact>
        </div>
      </Drawer>

      {/* Floating Chat Button */}
      {!chatOpen && (
        <div onClick={() => setChatOpen(true)}
          style={{
            position: 'fixed', bottom: 24, right: 24, width: 60, height: 60,
            borderRadius: 30, background: 'linear-gradient(135deg, #D4A03C, #B8860B)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 4px 20px rgba(212,160,60,0.4)',
            zIndex: 1000, transition: 'transform 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <MessageOutlined style={{ fontSize: 28, color: '#0A1F3F' }} />
        </div>
      )}
    </div>
  );
}
